'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import AppImage from '@/components/ui/AppImage';
import { Bookmark, Archive, Compass, SlidersHorizontal, Search, X, ChevronRight, Clock, MapPin, Crown } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type ItemType = 'article' | 'collection' | 'discovery';

interface LibraryItem {
  id: string;
  type: ItemType;
  category: string;
  collection?: string;
  title: string;
  subtitle: string;
  image: string;
  alt: string;
  location?: string;
  readTime?: number;
  savedDate: string;
  exclusivityLevel: 'Singular' | 'Rare' | 'Exclusive';
  isFirstSeen?: boolean;
}

// ─── Mock Library Data ────────────────────────────────────────────────────────

const libraryItems: LibraryItem[] = [
{
  id: 'lib-001',
  type: 'article',
  category: 'Objects',
  collection: 'Rare Horology',
  title: 'A. Lange & Söhne Grand Complication',
  subtitle: 'The only mechanical timepiece to combine a perpetual calendar, split-seconds chronograph and tourbillon in a single case under 15mm',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_17e2089ab-1772219942032.png',
  alt: 'Exquisite platinum pocket watch with intricate silver dial and multiple complications displayed on dark velvet',
  readTime: 9,
  savedDate: '14 Aug 2026',
  exclusivityLevel: 'Singular'
},
{
  id: 'lib-002',
  type: 'article',
  category: 'People',
  collection: 'Extraordinary People',
  title: 'Yusuf Al-Mansouri',
  subtitle: "The Qatari collector who has quietly assembled the world's most significant private collection of Ottoman manuscripts",
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a8da636c-1765338815557.png',
  alt: 'Distinguished middle-aged man in elegant dark suit seated in library surrounded by ancient leather-bound manuscripts',
  readTime: 18,
  savedDate: '13 Aug 2026',
  exclusivityLevel: 'Singular',
  isFirstSeen: true
},
{
  id: 'lib-003',
  type: 'article',
  category: 'Markets',
  collection: 'Collector Intelligence',
  title: 'Why Single-Malt Casks Are Outperforming Every Asset Class in 2026',
  subtitle: 'The three distilleries whose cask programs have returned 31–47% over 36 months',
  image: 'https://images.unsplash.com/photo-1603971729146-58cc08a42e76',
  alt: 'Row of aged oak whisky barrels in dark stone warehouse cellar with golden light filtering through wooden slats',
  readTime: 15,
  savedDate: '12 Aug 2026',
  exclusivityLevel: 'Exclusive'
},
{
  id: 'lib-004',
  type: 'discovery',
  category: 'Places',
  collection: 'Architectural Obscurities',
  title: 'The Derawar Fort at Night',
  subtitle: 'A 40-towered desert citadel with no visitors, no lighting, and no road on the map',
  image: 'https://images.unsplash.com/photo-1713682995521-22ec819b50ac',
  alt: 'Ancient desert fortress with cylindrical towers rising dramatically from golden sand dunes at sunset',
  location: 'Cholistan Desert, Pakistan',
  readTime: 11,
  savedDate: '11 Aug 2026',
  exclusivityLevel: 'Singular',
  isFirstSeen: true
},
{
  id: 'lib-005',
  type: 'collection',
  category: 'Collections',
  collection: 'Architectural Obscurities',
  title: 'Architectural Obscurities',
  subtitle: '23 extraordinary structures, hidden estates and forgotten palaces from across the private world',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_11bdf0f59-1772854306027.png',
  alt: 'Grand museum gallery with high ceilings displaying Old Master paintings in gilded frames under warm spotlights',
  savedDate: '10 Aug 2026',
  exclusivityLevel: 'Rare'
},
{
  id: 'lib-006',
  type: 'discovery',
  category: 'Experiences',
  collection: 'Private Dining Experiences',
  title: 'The Last Private Dinner at Noma',
  subtitle: 'René Redzepi hosts twelve guests, once a year, at a table that does not appear on any menu',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_164f17ebc-1776287874974.png',
  alt: 'Intimate restaurant dining room with candlelit table set for twelve with artful plating and Nordic decor',
  location: 'Copenhagen, Denmark',
  readTime: 7,
  savedDate: '9 Aug 2026',
  exclusivityLevel: 'Singular',
  isFirstSeen: true
},
{
  id: 'lib-007',
  type: 'collection',
  category: 'Collections',
  collection: 'Rare Horology',
  title: 'Rare Horology',
  subtitle: '11 timepieces that have appeared at auction fewer than three times in recorded history',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1df23c472-1783565148042.png",
  alt: 'Collection of rare vintage watches displayed on dark velvet with magnifying glass and watchmaker tools',
  savedDate: '8 Aug 2026',
  exclusivityLevel: 'Singular'
},
{
  id: 'lib-008',
  type: 'article',
  category: 'Places',
  collection: 'Architectural Obscurities',
  title: "Socotra's Dragon Blood Forest",
  subtitle: 'An island that evolved in complete isolation for 35 million years — accessible by private charter from Muscat',
  image: 'https://images.unsplash.com/photo-1708464437353-f3bc347ff5b3',
  alt: 'Surreal landscape with umbrella-shaped Dragon Blood trees against dramatic cloudy sky on isolated island',
  location: 'Socotra Island, Yemen',
  readTime: 10,
  savedDate: '7 Aug 2026',
  exclusivityLevel: 'Singular',
  isFirstSeen: true
},
{
  id: 'lib-009',
  type: 'discovery',
  category: 'Markets',
  collection: 'Collector Intelligence',
  title: 'The Quiet Correction in Hong Kong Jades',
  subtitle: 'Imperial-grade jadeite has declined 23% since 2023 — and the right buyers know exactly why',
  image: 'https://images.unsplash.com/photo-1606815216310-aead1e97b9b2',
  alt: 'Collection of luminous green imperial jade bangles and carved pendants displayed on white silk',
  readTime: 14,
  savedDate: '6 Aug 2026',
  exclusivityLevel: 'Rare'
},
{
  id: 'lib-010',
  type: 'article',
  category: 'Collections',
  collection: 'Architectural Obscurities',
  title: 'The Corsini Private Library, Florence',
  subtitle: '30,000 volumes — including 200 incunabula — in a palazzo that has never been photographed',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1154f3916-1772291203495.png',
  alt: 'Ancient library with floor-to-ceiling dark wood shelves filled with leather-bound books in Renaissance palazzo room',
  location: 'Florence, Italy',
  readTime: 17,
  savedDate: '5 Aug 2026',
  exclusivityLevel: 'Singular',
  isFirstSeen: true
}];


const ALL_CATEGORIES = ['All', 'Places', 'Objects', 'Experiences', 'Markets', 'People', 'Collections'];
const ALL_COLLECTIONS = ['All Collections', 'Architectural Obscurities', 'Rare Horology', 'Private Dining Experiences', 'Collector Intelligence', 'Extraordinary People'];
const ALL_TYPES: {label: string;value: ItemType | 'all';}[] = [
{ label: 'All', value: 'all' },
{ label: 'Articles', value: 'article' },
{ label: 'Collections', value: 'collection' },
{ label: 'Discoveries', value: 'discovery' }];


const exclusivityColors: Record<string, string> = {
  Singular: 'text-[var(--gold)] border-[var(--gold)]',
  Rare: 'text-amber-600 border-amber-400',
  Exclusive: 'text-stone-500 border-stone-400'
};

const typeIcon = (type: ItemType) => {
  if (type === 'article') return <Bookmark size={10} className="text-[var(--gold)]" />;
  if (type === 'collection') return <Archive size={10} className="text-blue-500" />;
  return <Compass size={10} className="text-emerald-600" />;
};

const typeBadgeStyle = (type: ItemType) => {
  if (type === 'article') return 'bg-amber-50 text-amber-700 border border-amber-200';
  if (type === 'collection') return 'bg-blue-50 text-blue-700 border border-blue-200';
  return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
};

const typeLabel = (type: ItemType) => {
  if (type === 'article') return 'Article';
  if (type === 'collection') return 'Collection';
  return 'Discovery';
};

// ─── Library Card ─────────────────────────────────────────────────────────────

function LibraryCard({ item, onRemove }: {item: LibraryItem;onRemove: (id: string) => void;}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="group relative bg-white border border-[var(--border)] rounded overflow-hidden flex flex-col transition-shadow hover:shadow-md"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      
      {/* Image */}
      <div className="relative aspect-[16/9] overflow-hidden bg-stone-100">
        <AppImage
          src={item.image}
          alt={item.alt}
          fill
          className={`object-cover transition-transform duration-500 ${hovered ? 'scale-105' : 'scale-100'}`} />
        
        {/* Overlay badges */}
        <div className="absolute top-2 left-2 flex gap-1.5 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold tracking-wide uppercase ${typeBadgeStyle(item.type)}`}>
            {typeIcon(item.type)}
            {typeLabel(item.type)}
          </span>
          {item.isFirstSeen &&
          <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold tracking-widest uppercase bg-[var(--gold)] text-white">
              First Seen
            </span>
          }
        </div>
        {/* Remove button */}
        <button
          onClick={() => onRemove(item.id)}
          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
          title="Remove from library">
          
          <X size={11} className="text-stone-500 hover:text-red-500" />
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[9px] font-bold tracking-[0.15em] uppercase text-[var(--slate-muted)]">{item.category}</span>
          <span className={`text-[9px] font-semibold tracking-wide uppercase border px-1.5 py-0.5 rounded ${exclusivityColors[item.exclusivityLevel]}`}>
            {item.exclusivityLevel}
          </span>
        </div>

        <Link href="/article" className="group/title">
          <h3 className="text-[13px] font-bold text-[var(--foreground)] leading-snug group-hover/title:text-[var(--gold)] transition-colors line-clamp-2 mb-1">
            {item.title}
          </h3>
        </Link>
        <p className="text-[11px] text-[var(--slate-muted)] leading-relaxed line-clamp-2 flex-1">{item.subtitle}</p>

        <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-[var(--border)]">
          <div className="flex items-center gap-2.5">
            {item.location &&
            <span className="flex items-center gap-1 text-[10px] text-[var(--slate-muted)]">
                <MapPin size={9} />
                {item.location.split(',')[0]}
              </span>
            }
            {item.readTime &&
            <span className="flex items-center gap-1 text-[10px] text-[var(--slate-muted)]">
                <Clock size={9} />
                {item.readTime} min
              </span>
            }
          </div>
          <span className="text-[9px] text-[var(--slate-muted)]">Saved {item.savedDate}</span>
        </div>

        {item.collection &&
        <div className="mt-1.5">
            <span className="inline-flex items-center gap-1 text-[9px] text-[var(--slate-muted)] bg-stone-50 border border-stone-200 px-1.5 py-0.5 rounded">
              <Archive size={8} />
              {item.collection}
            </span>
          </div>
        }
      </div>
    </div>);

}

// ─── Stats Bar ────────────────────────────────────────────────────────────────

function StatsBar({ items }: {items: LibraryItem[];}) {
  const articles = items.filter((i) => i.type === 'article').length;
  const collections = items.filter((i) => i.type === 'collection').length;
  const discoveries = items.filter((i) => i.type === 'discovery').length;

  return (
    <div className="flex items-center gap-6">
      <div className="flex items-center gap-1.5">
        <Bookmark size={12} className="text-amber-600" />
        <span className="text-[11px] font-semibold text-[var(--foreground)]">{articles}</span>
        <span className="text-[11px] text-[var(--slate-muted)]">Articles</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Archive size={12} className="text-blue-500" />
        <span className="text-[11px] font-semibold text-[var(--foreground)]">{collections}</span>
        <span className="text-[11px] text-[var(--slate-muted)]">Collections</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Compass size={12} className="text-emerald-600" />
        <span className="text-[11px] font-semibold text-[var(--foreground)]">{discoveries}</span>
        <span className="text-[11px] text-[var(--slate-muted)]">Discoveries</span>
      </div>
    </div>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MyLibraryPage() {
  const [items, setItems] = useState<LibraryItem[]>(libraryItems);
  const [activeType, setActiveType] = useState<ItemType | 'all'>('all');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeCollection, setActiveCollection] = useState('All Collections');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const matchType = activeType === 'all' || item.type === activeType;
      const matchCategory = activeCategory === 'All' || item.category === activeCategory;
      const matchCollection = activeCollection === 'All Collections' || item.collection === activeCollection;
      const matchSearch =
      !searchQuery ||
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.subtitle.toLowerCase().includes(searchQuery.toLowerCase());
      return matchType && matchCategory && matchCollection && matchSearch;
    });
  }, [items, activeType, activeCategory, activeCollection, searchQuery]);

  const handleRemove = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const hasActiveFilters =
  activeType !== 'all' || activeCategory !== 'All' || activeCollection !== 'All Collections' || searchQuery !== '';

  const clearFilters = () => {
    setActiveType('all');
    setActiveCategory('All');
    setActiveCollection('All Collections');
    setSearchQuery('');
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-white">
        {/* ── Page Header ── */}
        <div className="border-b border-[var(--border)] px-6 py-5 bg-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[9px] font-bold tracking-[0.2em] uppercase text-[var(--slate-muted)] mb-1">PRIVATE MEMBER</p>
              <h1 className="font-editorial text-[28px] font-bold text-[var(--foreground)] leading-none tracking-wide">
                My Library
              </h1>
              <p className="text-[12px] text-[var(--slate-muted)] mt-1.5">
                Your saved articles, collections and private discoveries — curated by you.
              </p>
            </div>
            <div className="flex items-center gap-3 mt-1">
              <Crown size={13} className="text-[var(--gold)]" />
              <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-[var(--gold)]">Private Member</span>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-4">
            <StatsBar items={items} />
          </div>
        </div>

        {/* ── Filter Bar ── */}
        <div className="sticky top-0 z-20 bg-white border-b border-[var(--border)] px-6 py-3">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[180px] max-w-[280px]">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--slate-muted)]" />
              <input
                type="text"
                placeholder="Search your library…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-7 pr-3 py-1.5 text-[11px] border border-[var(--border)] rounded bg-white text-[var(--foreground)] placeholder:text-[var(--slate-muted)] focus:outline-none focus:border-[var(--gold)] transition-colors" />
              
              {searchQuery &&
              <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X size={10} className="text-[var(--slate-muted)]" />
                </button>
              }
            </div>

            {/* Type tabs */}
            <div className="flex items-center gap-1 bg-stone-50 border border-[var(--border)] rounded p-0.5">
              {ALL_TYPES.map((t) =>
              <button
                key={t.value}
                onClick={() => setActiveType(t.value)}
                className={`px-2.5 py-1 text-[10px] font-semibold tracking-wide rounded transition-all ${
                activeType === t.value ?
                'bg-[var(--gold)] text-white shadow-sm' :
                'text-[var(--slate-muted)] hover:text-[var(--foreground)]'}`
                }>
                
                  {t.label}
                </button>
              )}
            </div>

            {/* Filters toggle */}
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold tracking-wide border rounded transition-all ${
              showFilters || activeCategory !== 'All' || activeCollection !== 'All Collections' ?
              'border-[var(--gold)] text-[var(--gold)] bg-amber-50' : 'border-[var(--border)] text-[var(--slate-muted)] hover:border-[var(--gold)] hover:text-[var(--gold)]'}`
              }>
              
              <SlidersHorizontal size={11} />
              Filters
            </button>

            {/* Clear filters */}
            {hasActiveFilters &&
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-[10px] text-[var(--slate-muted)] hover:text-red-500 transition-colors">
              
                <X size={10} />
                Clear all
              </button>
            }

            {/* Result count */}
            <span className="ml-auto text-[10px] text-[var(--slate-muted)]">
              {filtered.length} {filtered.length === 1 ? 'item' : 'items'}
            </span>
          </div>

          {/* Expanded filters */}
          {showFilters &&
          <div className="mt-3 pt-3 border-t border-[var(--border)] flex flex-wrap gap-4">
              {/* Category filter */}
              <div>
                <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-[var(--slate-muted)] mb-1.5">Category</p>
                <div className="flex flex-wrap gap-1">
                  {ALL_CATEGORIES.map((cat) =>
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-2.5 py-1 text-[10px] font-medium border rounded transition-all ${
                  activeCategory === cat ?
                  'border-[var(--gold)] bg-amber-50 text-[var(--gold)]' :
                  'border-[var(--border)] text-[var(--slate-muted)] hover:border-stone-300 hover:text-[var(--foreground)]'}`
                  }>
                  
                      {cat}
                    </button>
                )}
                </div>
              </div>

              {/* Collection filter */}
              <div>
                <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-[var(--slate-muted)] mb-1.5">Collection</p>
                <div className="flex flex-wrap gap-1">
                  {ALL_COLLECTIONS.map((col) =>
                <button
                  key={col}
                  onClick={() => setActiveCollection(col)}
                  className={`px-2.5 py-1 text-[10px] font-medium border rounded transition-all ${
                  activeCollection === col ?
                  'border-[var(--gold)] bg-amber-50 text-[var(--gold)]' :
                  'border-[var(--border)] text-[var(--slate-muted)] hover:border-stone-300 hover:text-[var(--foreground)]'}`
                  }>
                  
                      {col}
                    </button>
                )}
                </div>
              </div>
            </div>
          }
        </div>

        {/* ── Content ── */}
        <div className="px-6 py-6">
          {filtered.length === 0 ?
          <div className="flex flex-col items-center justify-center py-24 text-center">
              <Bookmark size={32} className="text-stone-200 mb-4" />
              <p className="text-[14px] font-semibold text-[var(--foreground)] mb-1">Nothing here yet</p>
              <p className="text-[12px] text-[var(--slate-muted)] max-w-[280px]">
                {hasActiveFilters ?
              'No items match your current filters. Try adjusting or clearing them.' : 'Start saving articles, collections and discoveries from the editorial feed.'}
              </p>
              {hasActiveFilters &&
            <button
              onClick={clearFilters}
              className="mt-4 px-4 py-2 text-[11px] font-semibold tracking-wide border border-[var(--gold)] text-[var(--gold)] rounded hover:bg-amber-50 transition-colors">
              
                  Clear Filters
                </button>
            }
              {!hasActiveFilters &&
            <Link
              href="/editorial-discovery-feed"
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold tracking-wide bg-[var(--gold)] text-white rounded hover:bg-[var(--gold-dark)] transition-colors">
              
                  Explore the Feed
                  <ChevronRight size={12} />
                </Link>
            }
            </div> :

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((item) =>
            <LibraryCard key={item.id} item={item} onRemove={handleRemove} />
            )}
            </div>
          }
        </div>
      </div>
    </AppLayout>);

}